import { Evaluation } from '@/models/evaluations';

export type Path = number[];

export type UIContentElement =
  | Evaluation.KeywordElement
  | Evaluation.KeywordGroupElement
  | UILogicalGroup;

export type UILogicalGroup = {
  type: 'logicalGroup';
  isNegated?: boolean;
  items: UIContentElement[];
  connectors: Evaluation.Operator[];
};

export const emptyGroup = (): UILogicalGroup => ({
  type: 'logicalGroup',
  isNegated: false,
  items: [],
  connectors: []
});

export const normalizeConnectors = (
  connectors: Evaluation.Operator[],
  itemCount: number
) => {
  const needed = Math.max(itemCount - 1, 0);
  const next: Evaluation.Operator[] = [];

  for (let i = 0; i < needed; i += 1) {
    next.push(connectors[i] || 'OR');
  }

  return next;
};

export const toggleOperator = (op: Evaluation.Operator): Evaluation.Operator =>
  op === 'AND' ? 'OR' : 'AND';

export const headerOperator = (
  connectors: Evaluation.Operator[]
): Evaluation.Operator | null => {
  if (!connectors.length) return null;
  return connectors.every((c) => c === connectors[0]) ? connectors[0] : null;
};

export const fromWire = (
  node?: Evaluation.LogicalGroupElement | null
): UILogicalGroup => {
  if (!node) return emptyGroup();

  const items: UIContentElement[] = [];
  const connectors: Evaluation.Operator[] = [];
  const fallback = node.operator || 'OR';

  node.elements.forEach((el) => {
    if (el.type === 'operator') connectors.push(el.value);
    else if (el.type === 'logicalGroup') items.push(fromWire(el));
    else items.push(el);
  });

  while (connectors.length < Math.max(items.length - 1, 0)) {
    connectors.push(fallback);
  }

  return { type: 'logicalGroup', isNegated: node.isNegated, items, connectors };
};

const toWireElement = (el: UIContentElement): Evaluation.QueryElement =>
  el.type === 'logicalGroup' ? toWire(el) : el;

export const toWire = (
  node: UILogicalGroup
): Evaluation.LogicalGroupElement => {
  const elements: Evaluation.QueryElement[] = [];

  node.items.forEach((el, i) => {
    elements.push(toWireElement(el));
    if (i < node.items.length - 1) {
      elements.push({ type: 'operator', value: node.connectors[i] || 'OR' });
    }
  });

  return { type: 'logicalGroup', isNegated: node.isNegated, elements };
};

export const updateAtPath = (
  node: UILogicalGroup,
  path: Path,
  fn: (g: UILogicalGroup) => UILogicalGroup
): UILogicalGroup => {
  if (!path.length) return fn(node);

  const [idx, ...rest] = path;

  return {
    ...node,
    items: node.items.map((el, i) =>
      i === idx && el.type === 'logicalGroup' ? updateAtPath(el, rest, fn) : el
    )
  };
};

export const splitPath = (path: Path) => ({
  parent: path.slice(0, -1),
  index: path[path.length - 1]
});

export const pathsEqual = (a: Path, b: Path) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export const isDescendantPath = (ancestor: Path, path: Path) =>
  path.length > ancestor.length && ancestor.every((v, i) => path[i] === v);

export const adjustPathAfterRemoval = (path: Path, removedPath: Path): Path => {
  const removedParent = removedPath.slice(0, -1);
  const removedIndex = removedPath[removedPath.length - 1];

  for (let depth = 0; depth < removedParent.length; depth += 1) {
    if (path[depth] !== removedParent[depth]) return path;
  }

  const idxAtDepth = path[removedParent.length];
  if (idxAtDepth === undefined || idxAtDepth <= removedIndex) return path;

  const next = [...path];
  next[removedParent.length] = idxAtDepth - 1;
  return next;
};

export const getItemAtPath = (
  node: UILogicalGroup,
  path: Path
): UIContentElement => {
  const [idx, ...rest] = path;
  const child = node.items[idx];
  if (!rest.length) return child;
  if (child.type !== 'logicalGroup') {
    throw new Error(`Invalid path: ${path.join('-')}`);
  }
  return getItemAtPath(child, rest);
};

export const addItem = (
  node: UILogicalGroup,
  path: Path,
  item: UIContentElement
) =>
  updateAtPath(node, path, (g) => {
    const items = [...g.items, item];
    return {
      ...g,
      items,
      connectors: normalizeConnectors(g.connectors, items.length)
    };
  });

export const insertItemAt = (
  node: UILogicalGroup,
  path: Path,
  index: number,
  item: UIContentElement
) =>
  updateAtPath(node, path, (g) => {
    const items = [...g.items.slice(0, index), item, ...g.items.slice(index)];
    return {
      ...g,
      items,
      connectors: normalizeConnectors(g.connectors, items.length)
    };
  });

export const removeItem = (node: UILogicalGroup, path: Path) => {
  const { parent, index } = splitPath(path);

  return updateAtPath(node, parent, (g) => ({
    ...g,
    items: g.items.filter((_, i) => i !== index),
    connectors: g.connectors.filter(
      (_, i) => i !== (index === 0 ? 0 : index - 1)
    )
  }));
};

export const patchItem = (
  node: UILogicalGroup,
  path: Path,
  patch: Partial<UIContentElement>
) => {
  const { parent, index } = splitPath(path);

  return updateAtPath(node, parent, (g) => ({
    ...g,
    items: g.items.map((el, i) =>
      i === index ? ({ ...el, ...patch } as UIContentElement) : el
    )
  }));
};

export const moveItem = (
  root: UILogicalGroup,
  fromPath: Path,
  toParentPath: Path,
  toIndex: number
): UILogicalGroup => {
  const targetSlot = [...toParentPath, toIndex];

  if (pathsEqual(fromPath, toParentPath)) return root;
  if (isDescendantPath(fromPath, toParentPath)) return root;
  if (pathsEqual(fromPath, targetSlot)) return root;

  const item = getItemAtPath(root, fromPath);
  const withoutItem = removeItem(root, fromPath);

  const adjustedSlot = adjustPathAfterRemoval(targetSlot, fromPath);
  const { parent: adjustedParent, index: adjustedIndex } =
    splitPath(adjustedSlot);

  return insertItemAt(withoutItem, adjustedParent, adjustedIndex, item);
};

export const setConnector = (
  node: UILogicalGroup,
  path: Path,
  index: number,
  operator: Evaluation.Operator
) =>
  updateAtPath(node, path, (g) => ({
    ...g,
    connectors: g.connectors.map((c, i) => (i === index ? operator : c))
  }));

export const setAllConnectors = (
  node: UILogicalGroup,
  path: Path,
  operator: Evaluation.Operator
) =>
  updateAtPath(node, path, (g) => ({
    ...g,
    connectors: g.connectors.map(() => operator)
  }));

export const parseKeywordsInput = (input: string): string[] => {
  const result: string[] = [];

  input.split(',').forEach((k) => {
    const val = k.trim().toLowerCase();
    if (val && !result.includes(val)) result.push(val);
  });

  return result;
};

export const pathKey = (path: Path) => (path.length ? path.join('-') : 'root');
